import type { CrawlJobStatus } from '../../types/lac';
import type { PageSize } from '../../types/userConfig';
import { PAGE_SIZES } from '../../types/userConfig';
import { dateInputToUnixMillis } from '../../utils/dateTimeUtils';

export const LAC_WEBSITE_CRAWLS_SEARCH_DEBOUNCE_MS = 300;
export const ALL_TENANTS_FILTER = 'all';
export const ALL_STATUS_FILTER = 'all';
export const DEFAULT_LAC_WEBSITE_CRAWLS_PAGE_SIZE: PageSize = 10;

export const CRAWL_JOB_STATUS_FILTERS = [
  'MAPPING',
  'AWAITING_SELECTION',
  'CRAWLING',
  'PROCESSING',
  'COMPLETED',
  'FAILED',
] as const satisfies readonly CrawlJobStatus[];

export type LacWebsiteCrawlsStatusFilter =
  typeof ALL_STATUS_FILTER | CrawlJobStatus;

export type LacWebsiteCrawlsFilterState = {
  debouncedStartUrl: string | undefined;
  tenant: string;
  status: LacWebsiteCrawlsStatusFilter;
  fromDay: string;
  toDay: string;
};

export function isCrawlJobStatus(value: string): value is CrawlJobStatus {
  return CRAWL_JOB_STATUS_FILTERS.some((status) => status === value);
}

export function toStartUrlFilter(raw: string): string | undefined {
  const trimmed = raw.trim();
  if (trimmed === '') {
    return undefined;
  }
  if (trimmed.includes('*') || trimmed.includes('?')) {
    return trimmed;
  }
  return `*${trimmed}*`;
}

export function tenantOptionLabel(tenant: {
  name: string;
  displayName?: string | null;
}): string {
  return tenant.displayName?.trim() || tenant.name;
}

function parseDayParam(searchParams: URLSearchParams, key: string): string {
  const value = searchParams.get(key)?.trim() ?? '';
  return dateInputToUnixMillis(value) !== undefined ? value : '';
}

export function parseLacWebsiteCrawlsFilterFromSearchParams(
  searchParams: URLSearchParams,
  { includeTenant }: { includeTenant: boolean },
): LacWebsiteCrawlsFilterState {
  const search = searchParams.get('search')?.trim() ?? '';
  const tenantRaw = searchParams.get('tenant')?.trim() ?? '';
  const statusRaw = searchParams.get('status')?.trim() ?? '';
  const fromDay = parseDayParam(searchParams, 'from');
  const toDay = parseDayParam(searchParams, 'to');

  const tenant =
    includeTenant && tenantRaw !== '' && tenantRaw !== ALL_TENANTS_FILTER
      ? tenantRaw
      : ALL_TENANTS_FILTER;

  const status: LacWebsiteCrawlsStatusFilter = isCrawlJobStatus(statusRaw)
    ? statusRaw
    : ALL_STATUS_FILTER;

  return {
    debouncedStartUrl: search === '' ? undefined : toStartUrlFilter(search),
    tenant,
    status,
    fromDay,
    toDay: fromDay !== '' && toDay !== '' && toDay >= fromDay ? toDay : '',
  };
}

export function parseLacWebsiteCrawlsPageFromSearchParams(
  searchParams: URLSearchParams,
): number {
  const pageValue = Number(searchParams.get('page') ?? '1');
  if (!Number.isFinite(pageValue) || pageValue < 1) {
    return 1;
  }
  return Math.floor(pageValue);
}

export function parseLacWebsiteCrawlsPageSizeFromSearchParams(
  searchParams: URLSearchParams,
): PageSize {
  const raw = Number(searchParams.get('pageSize') ?? '');
  if (PAGE_SIZES.some((size) => size === raw)) {
    return raw as PageSize;
  }
  return DEFAULT_LAC_WEBSITE_CRAWLS_PAGE_SIZE;
}

export function buildLacWebsiteCrawlsSearchParams({
  searchInput,
  tenant,
  status,
  fromDay,
  toDay,
  page,
  pageSize,
  includeTenant,
}: {
  searchInput: string;
  tenant: string;
  status: LacWebsiteCrawlsStatusFilter;
  fromDay: string;
  toDay: string;
  page: number;
  pageSize: PageSize;
  includeTenant: boolean;
}): URLSearchParams {
  const params = new URLSearchParams();

  const trimmedSearch = searchInput.trim();
  if (trimmedSearch !== '') {
    params.set('search', trimmedSearch);
  }

  if (includeTenant && tenant !== ALL_TENANTS_FILTER && tenant.trim() !== '') {
    params.set('tenant', tenant.trim());
  }

  if (status !== ALL_STATUS_FILTER) {
    params.set('status', status);
  }

  if (fromDay !== '' && toDay !== '' && toDay >= fromDay) {
    params.set('from', fromDay);
    params.set('to', toDay);
  }

  if (page > 1) {
    params.set('page', String(page));
  }

  if (pageSize !== DEFAULT_LAC_WEBSITE_CRAWLS_PAGE_SIZE) {
    params.set('pageSize', String(pageSize));
  }

  return params;
}
